/**
 * Wiki URL 契约：SPA（react-router）与爬虫 SSR（App Router）必须使用同一套 path。
 * 改路径时先改这里，再同步 spa/router 与 app/ 路由。
 */

export const GAME_SHELL_SEGMENTS = [
  'dcatalog',
  'dguides',
  'dtools',
  'dcommunity',
  'dbenefits',
] as const;

export type GameShellSegment = (typeof GAME_SHELL_SEGMENTS)[number];

export function wikiGameHomePath(game: string) {
  return `/${encodeURIComponent(game)}`;
}

export function wikiShellPath(game: string, segment: GameShellSegment) {
  return `/${encodeURIComponent(game)}/${segment}`;
}

export function wikiCatalogPath(game: string, catalogKey?: string) {
  const base = wikiShellPath(game, 'dcatalog');
  return catalogKey ? `${base}/${encodeURIComponent(catalogKey)}` : base;
}

export function wikiGuidesPath(game: string) {
  return wikiShellPath(game, 'dguides');
}

export function wikiGuidePostPath(game: string, postId: string) {
  return `/${encodeURIComponent(game)}/guide/${encodeURIComponent(postId)}`;
}

export function wikiHeroPath(game: string, heroId: string) {
  const id = String(heroId).replace(/^t/i, '');
  return `/${encodeURIComponent(game)}/t${encodeURIComponent(id)}`;
}

export function wikiSearchPath(game: string, query?: string) {
  const base = `/${encodeURIComponent(game)}/search`;
  if (!query?.trim()) return base;
  return `${base}?q=${encodeURIComponent(query.trim())}`;
}

export function wikiProfilePath(game: string) {
  return `/${encodeURIComponent(game)}/profile`;
}

export function wikiCommunityCreatePath(game: string, preset?: 'announcement' | 'information') {
  const base = `/${encodeURIComponent(game)}/dcommunity/create`;
  return preset ? `${base}?preset=${preset}` : base;
}

export function wikiGuideCreatePath(game: string) {
  return `/${encodeURIComponent(game)}/dguides/create`;
}

export function wikiGuideEditPath(game: string, postId: string) {
  return `/${encodeURIComponent(game)}/guide/${encodeURIComponent(postId)}/edit`;
}

export function wikiUserPostsPath(uid: string | number) {
  return `/user/${encodeURIComponent(String(uid))}`;
}

export function wikiGenericCatalogItemPath(
  game: string,
  detailPrefix: string,
  itemId: string | number,
) {
  return `/${encodeURIComponent(game)}/${encodeURIComponent(detailPrefix)}${encodeURIComponent(String(itemId))}`;
}

/** 需要爬虫 SSR 出完整 HTML 的路径模式（与 SPA 路由一一对应）。 */
export const SEO_SSR_PATH_PATTERNS = [
  '/',
  '/wikis',
  '/updates',
  '/characters',
  '/profile',
  '/user/:uid',
  '/:game',
  '/:game/dcatalog',
  '/:game/dcatalog/:catalog',
  '/:game/dguides',
  '/:game/dtools',
  '/:game/dcommunity',
  '/:game/dbenefits',
  '/:game/guide/:postId',
  '/:game/guide/:postId/edit',
  '/:game/dcommunity/create',
  '/:game/dguides/create',
  '/:game/t:heroId',
  '/:game/:prefixItemId',
  '/:game/search',
  '/:game/profile',
] as const;

/** 第一期 SPA 已挂载的路径。 */
export const SPA_PHASE1_PATH_PATTERNS = [
  '/:game',
  '/:game/dcatalog',
  '/:game/dcatalog/:catalog',
  '/:game/dguides',
  '/:game/dtools',
  '/:game/dcommunity',
  '/:game/dbenefits',
  '/:game/guide/:postId',
] as const;

/** 第二期追加：角色详情 + 搜索。 */
export const SPA_PHASE2_PATH_PATTERNS = [
  '/:game/t:heroId',
  '/:game/search',
] as const;

/** 第三期追加：个人中心 + 发帖/编辑。 */
export const SPA_PHASE3_PATH_PATTERNS = [
  '/:game/profile',
  '/:game/dcommunity/create',
  '/:game/dguides/create',
  '/:game/guide/:postId/edit',
] as const;

/** 第四期追加：门户 + 平台账号 + 通用图鉴深链。 */
export const SPA_PHASE4_PATH_PATTERNS = [
  '/',
  '/wikis',
  '/updates',
  '/characters',
  '/profile',
  '/user/:uid',
  '/:game/:prefixItemId',
] as const;

/** 当前 SPA 已挂载的全部路径。 */
export const SPA_MOUNTED_PATH_PATTERNS = [
  ...SPA_PHASE1_PATH_PATTERNS,
  ...SPA_PHASE2_PATH_PATTERNS,
  ...SPA_PHASE3_PATH_PATTERNS,
  ...SPA_PHASE4_PATH_PATTERNS,
] as const;
